import React, { useState, useEffect } from 'react'
import { 
  Activity, 
  Bot, 
  CheckCircle, 
  AlertCircle, 
  Clock,
  Zap,
  TrendingUp,
  Users
} from 'lucide-react'
import { apiFetch } from '../config'

const Dashboard = () => {
  const [metrics, setMetrics] = useState({
    totalAgents: 0,
    activeAgents: 0,
    idleAgents: 0,
    errorAgents: 0,
    activity24h: 0,
    systemHealth: 'healthy'
  })
  const [recentActivity, setRecentActivity] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchMetrics()
    fetchRecentActivity()
    
    // Refresh every 30 seconds
    const interval = setInterval(() => {
      fetchMetrics()
      fetchRecentActivity()
    }, 30000)
    
    return () => clearInterval(interval)
  }, [])

  const fetchMetrics = async () => {
    try {
      const response = await apiFetch("/metrics")
      const data = await response.json()
      
      // Calculate from agent_status array
      const statusCounts = data.agent_status?.reduce((acc, curr) => {
        acc[curr.status] = curr.count
        return acc
      }, {}) || {}
      
      setMetrics({
        totalAgents: Object.values(statusCounts).reduce((a, b) => a + b, 0),
        activeAgents: statusCounts.active || 0,
        idleAgents: statusCounts.idle || 0,
        errorAgents: (statusCounts.error || 0) + (statusCounts.degraded || 0),
        activity24h: data.activity_24h || 0,
        systemHealth: data.status || 'healthy'
      })
      
      setLoading(false)
    } catch (error) {
      console.error('Failed to fetch metrics:', error)
    }
  }

  const fetchRecentActivity = async () => {
    try {
      const response = await apiFetch("/activity?limit=10")
      const data = await response.json()
      setRecentActivity(data)
    } catch (error) {
      console.error('Failed to fetch activity:', error)
    }
  }

  const MetricCard = ({ title, value, subtitle, icon: Icon, color }) => (
    <div className="metric-card">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-text-secondary text-sm mb-1">{title}</p>
          <p className="metric-value">{value}</p>
          {subtitle && <p className="text-xs text-text-secondary mt-2">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Dashboard</h1>
          <p className="text-text-secondary mt-1">
            Monitor your AI agent fleet in real-time
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
          <span className="text-sm text-text-secondary">All systems operational</span>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Agents"
          value={metrics.totalAgents}
          subtitle="14 configured agents"
          icon={Bot}
          color="bg-accent/10 text-accent"
        />
        
        <MetricCard
          title="Active Now"
          value={metrics.activeAgents}
          subtitle={`${Math.round((metrics.activeAgents / metrics.totalAgents) * 100)}% of fleet`}
          icon={Activity}
          color="bg-success/10 text-success"
        />
        
        <MetricCard
          title="24h Activity"
          value={metrics.activity24h}
          subtitle="Events processed"
          icon={TrendingUp}
          color="bg-purple-500/10 text-purple-500"
        />
        
        <MetricCard
          title="System Health"
          value={metrics.errorAgents === 0 ? '100%' : `${Math.round(((metrics.totalAgents - metrics.errorAgents) / metrics.totalAgents) * 100)}%`}
          subtitle={metrics.errorAgents > 0 ? `${metrics.errorAgents} agents need attention` : 'All agents healthy'}
          icon={metrics.errorAgents > 0 ? AlertCircle : CheckCircle}
          color={metrics.errorAgents > 0 ? "bg-danger/10 text-danger" : "bg-success/10 text-success"}
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Agent Status Breakdown */}
        <div className="lg:col-span-2 card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Agent Status Breakdown</h2>
            <button className="text-sm text-accent hover:text-accent-hover">
              View All
            </button>
          </div>
          
          <div className="space-y-3">
            {[
              { label: 'Active', count: metrics.activeAgents, color: 'bg-success', icon: Activity },
              { label: 'Idle', count: metrics.idleAgents, color: 'bg-info', icon: Clock },
              { label: 'Paused', count: 0, color: 'bg-warning', icon: Clock },
              { label: 'Error/Degraded', count: metrics.errorAgents, color: 'bg-danger', icon: AlertCircle },
            ].map((status) => (
              <div key={status.label} className="flex items-center justify-between p-3 bg-surface-elevated rounded-lg">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${status.color}`} />
                  <span className="text-text-secondary">{status.label}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="font-mono font-semibold">{status.count}</span>
                  <div className="w-32 h-2 bg-border rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${status.color} transition-all`}
                      style={{ 
                        width: `${metrics.totalAgents > 0 ? (status.count / metrics.totalAgents) * 100 : 0}%` 
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Recent Activity</h2>
            <Activity className="w-5 h-5 text-text-secondary" />
          </div>
          
          <div className="space-y-3 max-h-80 overflow-auto">
            {recentActivity.length === 0 ? (
              <p className="text-text-secondary text-center py-8">No recent activity</p>
            ) : (
              recentActivity.slice(0, 8).map((activity, index) => (
                <div key={index} className="flex items-start gap-3 p-3 bg-surface-elevated rounded-lg">
                  <div className="w-2 h-2 rounded-full bg-accent mt-2" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{activity.agent_name}</p>
                    <p className="text-xs text-text-secondary">{activity.event_type}</p>
                    <p className="text-xs text-text-muted mt-1">
                      {new Date(activity.timestamp).toLocaleTimeString()}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button className="flex items-center gap-3 p-4 bg-surface-elevated rounded-lg hover:bg-surface transition-colors text-left">
            <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
              <Zap className="w-5 h-5 text-accent" />
            </div>
            <div>
              <p className="font-medium">Run Workflow</p>
              <p className="text-sm text-text-secondary">Execute Antfarm workflow</p>
            </div>
          </button>
          
          <button className="flex items-center gap-3 p-4 bg-surface-elevated rounded-lg hover:bg-surface transition-colors text-left">
            <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-success" />
            </div>
            <div>
              <p className="font-medium">Manage Agents</p>
              <p className="text-sm text-text-secondary">View all 14 agents</p>
            </div>
          </button>
          
          <button className="flex items-center gap-3 p-4 bg-surface-elevated rounded-lg hover:bg-surface transition-colors text-left">
            <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center">
              <Activity className="w-5 h-5 text-purple-500" />
            </div>
            <div>
              <p className="font-medium">View Neural Map</p>
              <p className="text-sm text-text-secondary">Visual agent relationships</p>
            </div>
          </button>
        </div>
      </div>
    </div>
  )
}

export default Dashboard
